import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { useCopilotStyles } from './styles';

import { useSessionContext } from '../../contexts/SessionContext';
import useCopilot from '../../hooks/useCopilot';
import useSession from '../../hooks/useSession';

import { COLORS } from '../../utils/colors';

const CopilotVision = () => {
  const styles = useCopilotStyles();

  const {
    getImageAsBase64,
    modelSelect,
    setModelSelect,
    modelList,
    analyzeImage,
  } = useCopilot();
  const { updateImage } = useSession();
  const { booking, selectedImage } = useSessionContext();

  const [query, setQuery] = useState<string>('');
  const [analysingImage, setAnalyzingImage] = useState<boolean>(false);
  const [analyzingImageError, setAnalysingImageError] =
    useState<boolean>(false);

  const [enableSave, setEnableSave] = useState<boolean>(false);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnalysingImageError(false);
    setQuery(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAnalyzeImage();
    }
  };

  const handleAnalyzeImage = async () => {
    if (!selectedImage || !query) return;

    try {
      setAnalyzingImage(true);
      setAnalysingImageError(false);

      const response = await analyzeImage({
        image: await getImageAsBase64(`${selectedImage.src}`),
        query,
        model: modelList[modelSelect],
      });

      if (response) {
        if (!selectedImage.description) selectedImage.description = [];
        selectedImage.description.push({
          query,
          model: modelList[modelSelect],
          response,
        });
      }

      setEnableSave(true);
      setAnalyzingImage(false);
    } catch (error) {
      setAnalyzingImage(false);
      setAnalysingImageError(true);
      console.error('Error analyzing image:', error);
    }
  };

  useEffect(() => {
    setAnalysingImageError(false);
  }, [selectedImage]);

  const handleSaveImage = async () => {
    if (!booking || !selectedImage?.description) return;

    await updateImage({
      bookingId: booking.booking_id,
      image: { id: selectedImage.id, description: selectedImage.description },
    });
    setEnableSave(false);
  };

  return (
    <>
      <Box style={{ marginTop: '1rem', minHeight: '3rem' }}>
        <FormControl sx={{ float: 'right', minWidth: '10rem' }}>
          <InputLabel
            id="select-label"
            shrink
            sx={{ backgroundColor: '#eee', pl: '.5rem', pr: '.5rem' }}>
            Model
          </InputLabel>
          <Select
            labelId="select-label"
            sx={styles.select}
            value={modelSelect}
            onChange={(e) => setModelSelect(Number(e.target.value))}>
            {modelList.map((model, index) => (
              <MenuItem key={index} value={index}>
                {model.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box>
        <TextField
          sx={{ ...styles.textField, maxWidth: 'calc(100% - 7.5rem)' }}
          label="Question about selected image"
          value={query}
          onChange={handleQueryChange}
          onKeyPress={handleKeyPress}
          variant="outlined"
          placeholder="Type your question here"
        />
        <Button
          sx={styles.buttonAnalyze}
          onClick={handleAnalyzeImage}
          disabled={!selectedImage || !query}>
          {analyzingImageError ? (
            'Error !'
          ) : analysingImage ? (
            <CircularProgress size={20} sx={{ color: COLORS.PRIMARY }} />
          ) : (
            'Analyze'
          )}
        </Button>
        {enableSave && (
          <Button
            sx={styles.buttonAnalyze}
            onClick={handleSaveImage}
            disabled={!selectedImage || !query}>
            Save
          </Button>
        )}
      </Box>
      <Box>
        {selectedImage?.description?.map((item, index) => {
          return (
            <Box key={index}>
              <Typography
                sx={{ fontStyle: 'italic', color: '#00a', pt: '1.25rem' }}
                pb=".25rem">
                {item.query}
              </Typography>
              <Typography>{item.response}</Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default CopilotVision;
